import { CandidatoService, ReadCandidatoDto } from './candidato.service';
import { CursoService } from '@/modules/structure/curso/curso.service';
import { ProcessoSeletivoService } from '../processo-seletivo/processo-seletivo.service';
import { InstituicaoEnsinoService } from '@/modules/structure/instituicoes/instituicao.service';
import { toast } from 'react-toastify';
import { useEffect, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import Links from '@/shared/constants/navegacao';
import api from '@/modules/api.service';
import { Endpoints } from '@/shared/constants/endpoints';

const candidatoService = new CandidatoService();
const processoSeletivoService = new ProcessoSeletivoService();
const cursoService = new CursoService();
const instituicaoEnsinoService = new InstituicaoEnsinoService();

export default function CandidatoDetalhesPage() {
  const navigate = useNavigate();
  const params = useParams();
  const candidatoId = params.id ? Number(params.id) : 0;

  const [candidato, setCandidato] = useState<ReadCandidatoDto | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [processoSeletivo, setProcessoSeletivo] = useState<{ nome: string } | null>(null);
  const [cursoPretendido, setCursoPretendido] = useState<{ nome: string } | null>(null);
  const [instituicaoEnsino, setInstituicaoEnsino] = useState<{ razao_social: string } | null>(null);
  const [, setCurso] = useState<{ nome: string } | null>(null);

  useEffect(() => {
    if (candidatoId > 0) {
      setIsLoading(true);
      candidatoService.findOne(candidatoId)
        .then((result) => {
          setCandidato(result);

          // Carregar dados relacionados
          if (result.processoSeletivoId) {
            processoSeletivoService.findOne(result.processoSeletivoId)
              .then((ps) => setProcessoSeletivo({ nome: ps.nome }))
              .catch(() => { });
          }

          if (result.cursoPretendidoId) {
            cursoService.findOne(result.cursoPretendidoId)
              .then((c) => setCursoPretendido({ nome: c.nome }))
              .catch(() => { });
          }

          if (result.instituicaoEnsinoId) {
            instituicaoEnsinoService.Read(result.instituicaoEnsinoId)
              .then((inst) => {
                if (inst) setInstituicaoEnsino({ razao_social: inst.razao_social });
              })
              .catch(() => { });
          }

          if (result.cursoId) {
            cursoService.findOne(result.cursoId)
              .then((c) => setCurso({ nome: c.nome }))
              .catch(() => { });
          }
        })
        .catch((error) => {
          toast.error('Falha ao carregar dados do candidato: ' + error);
          navigate('/');
        })
        .finally(() => setIsLoading(false));
    }
  }, [candidatoId, navigate]);

  const formatarDataHora = (data: Date | string) => {
    if (!data) return '-';
    return new Date(data).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatarSituacao = (situacao: string) => {
    const situacaoMap: Record<string, string> = {
      'inscricao_realizada': 'Inscrição Realizada',
      'inscricao_validada': 'Inscrição Validada',
      'indeferida': 'Indeferida',
      'classificada': 'Classificada',
      'convocada': 'Convocada',
    };
    return situacaoMap[situacao] || situacao;
  };

  const formatarGenero = (genero: string) => {
    return genero === 'masculino' ? 'Masculino' : genero === 'feminino' ? 'Feminino' : genero;
  };

  const formatarRaca = (raca: string) => {
    const racaMap: Record<string, string> = {
      'branca': 'Branca',
      'preta': 'Preta',
      'parda': 'Parda',
      'amarela': 'Amarela',
      'indigena': 'Indígena',
    };
    return racaMap[raca] || raca;
  };

  const handleDownloadFile = async (fileId: string, filename: string) => {
    try {
      const response = await api.get(Endpoints.CANDIDATO + '/file/' + fileId, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'arquivo.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Erro ao baixar arquivo.');
    }
  };

  const handleViewFile = async (fileId: string) => {
    try {
      const response = await api.get(Endpoints.CANDIDATO + '/file/' + fileId, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
    } catch (error) {
      toast.error('Erro ao abrir arquivo.');
    }
  };

  if (isLoading) {
    return (
      <div className="row">
        <div className="col-12 text-center">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
        </div>
      </div>
    );
  }

  if (!candidato) {
    return (
      <div className="row">
        <div className="col-12">
          <p>Candidato não encontrado.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-md-8">
        <h3 className="pt-4">Detalhes do Candidato</h3>
        {processoSeletivo && (
          <span className="blockquote-footer pb-4 text-end">
            Processo Seletivo: {processoSeletivo.nome}
          </span>
        )}
      </div>
      <div className="col-md-4">
        <br />
        <Link
          type="button"
          className="btn btn-secondary float-end"
          to={`${Links.Candidato_Index}/${candidato.processoSeletivoId}`}
        >
          Voltar
        </Link>
      </div>
      <hr className="pb-4" />

      {/* DADOS PESSOAIS */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Identificação Pessoal</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="mb-3 col-md-12">
              <label className="form-label fw-bold">Nome Completo:</label>
              <p>{candidato.pessoaNome || '-'}</p>
            </div>

            {candidato.nomeSocial && (
              <div className="mb-3 col-md-12">
                <label className="form-label fw-bold">Nome Social:</label>
                <p>{candidato.nomeSocial}</p>
              </div>
            )}

            <div className="mb-3 col-md-3">
              <label className="form-label fw-bold">Número do Candidato:</label>
              <p>{candidato.id || '-'}</p>
            </div>

            <div className="mb-3 col-md-3">
              <label className="form-label fw-bold">CPF:</label>
              <p>{candidato.pessoaCpf || '-'}</p>
            </div>

            <div className="mb-3 col-md-3">
              <label className="form-label fw-bold">E-mail:</label>
              <p>{candidato.pessoaEmail || '-'}</p>
            </div>

            <div className="mb-3 col-md-3">
              <label className="form-label fw-bold">Gênero:</label>
              <p>{formatarGenero(candidato.genero)}</p>
            </div>

            <div className="mb-3 col-md-12">
              <label className="form-label fw-bold">Documento de Identificação:</label>
              {candidato.documentoIdentificacaoUrl ? (
                <div>
                  <button
                    className="btn btn-sm btn-primary me-2"
                    onClick={() => handleViewFile(candidato.documentoIdentificacaoUrl!)}
                  >
                    Visualizar
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleDownloadFile(candidato.documentoIdentificacaoUrl!, 'documento_identificacao.pdf')}
                  >
                    Baixar
                  </button>
                </div>
              ) : (
                <p className="text-muted">Nenhum arquivo anexado</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* DADOS ACADÊMICOS */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Dados Acadêmicos</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="mb-3 col-md-6">
              <label className="form-label fw-bold">Instituição de Ensino:</label>
              <p>{instituicaoEnsino?.razao_social || candidato.instituicaoEnsinoNome || '-'}</p>
            </div>

            <div className="mb-3 col-md-6">
              <label className="form-label fw-bold">Período Atual do Curso:</label>
              <p>{candidato.periodoAtualCurso || '-'}</p>
            </div>

            <div className="mb-3 col-md-6">
              <label className="form-label fw-bold">Ano de Início do Curso:</label>
              <p>{candidato.anoInicioCurso || '-'}</p>
            </div>

            <div className="mb-3 col-md-12">
              <label className="form-label fw-bold">Declaração de Matrícula:</label>
              {candidato.documentoEstudoUrl ? (
                <div>
                  <button
                    className="btn btn-sm btn-primary me-2"
                    onClick={() => handleViewFile(candidato.documentoEstudoUrl!)}
                  >
                    Visualizar
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleDownloadFile(candidato.documentoEstudoUrl!, 'documento_estudo.pdf')}
                  >
                    Baixar
                  </button>
                </div>
              ) : (
                <p className="text-muted">Nenhum arquivo anexado</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ENDEREÇO */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Endereço</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="mb-3 col-md-12">
              <label className="form-label fw-bold">Comprovante de Residência:</label>
              {candidato.comprovanteResidenciaUrl ? (
                <div>
                  <button
                    className="btn btn-sm btn-primary me-2"
                    onClick={() => handleViewFile(candidato.comprovanteResidenciaUrl!)}
                  >
                    Visualizar
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleDownloadFile(candidato.comprovanteResidenciaUrl!, 'comprovante_residencia.pdf')}
                  >
                    Baixar
                  </button>
                </div>
              ) : (
                <p className="text-muted">Nenhum arquivo anexado</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* CURSO E MUNICÍPIO DE INTERESSE */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Curso e Município de Interesse</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="mb-3 col-md-6">
              <label className="form-label fw-bold">Curso Pretendido:</label>
              <p>{cursoPretendido?.nome || '-'}</p>
            </div>

            <div className="mb-3 col-md-6">
              <label className="form-label fw-bold">Município:</label>
              <p>{candidato.municipio || '-'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* IDENTIFICAÇÃO ÉTNICO-RACIAL */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Identificação Étnico-Racial (Ações Afirmativas)</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="mb-3 col-md-6">
              <label className="form-label fw-bold">Identificação Racial:</label>
              <p>{formatarRaca(candidato.raca)}</p>
            </div>

            <div className="mb-3 col-md-6">
              <label className="form-label fw-bold">Opta por concorrer às vagas reservadas para pessoas negras?</label>
              <p>{candidato.optaVagasNegros ? 'Sim' : 'Não'}</p>
            </div>

            <div className="mb-3 col-md-6">
              <label className="form-label fw-bold">Declara-se indígena?</label>
              <p>{candidato.declaraIndigena ? 'Sim' : 'Não'}</p>
            </div>

            {candidato.documentacaoIndigenaUrl && (
              <div className="mb-3 col-md-6">
                <label className="form-label fw-bold">Documentação Comprobatória Indígena:</label>
                <div>
                  <button
                    className="btn btn-sm btn-primary me-2"
                    onClick={() => handleViewFile(candidato.documentacaoIndigenaUrl!)}
                  >
                    Visualizar
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleDownloadFile(candidato.documentacaoIndigenaUrl!, 'documentacao_indigena.pdf')}
                  >
                    Baixar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* PESSOA COM DEFICIÊNCIA */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Pessoa com Deficiência (PcD)</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="mb-3 col-md-6">
              <label className="form-label fw-bold">Opta por concorrer às vagas reservadas para pessoa com deficiência?</label>
              <p>{candidato.optaVagasPcd ? 'Sim' : 'Não'}</p>
            </div>

            {candidato.optaVagasPcd && (
              <>
                <div className="mb-3 col-md-6">
                  <label className="form-label fw-bold">Descrição da deficiência:</label>
                  <p>{candidato.descricaoDeficiencia || '-'}</p>
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label fw-bold">Especifique sua deficiência:</label>
                  <p>{candidato.especificacaoDeficiencia || '-'}</p>
                </div>

                <div className="mb-3 col-md-6">
                  <label className="form-label fw-bold">Necessita de cuidados especiais para realização da prova?</label>
                  <p>{candidato.necessitaCuidadosEspeciais ? 'Sim' : 'Não'}</p>
                </div>
              </>
            )}

            {candidato.laudoMedicoUrl && (
              <div className="mb-3 col-md-6">
                <label className="form-label fw-bold">Laudo Médico:</label>
                <div>
                  <button
                    className="btn btn-sm btn-primary me-2"
                    onClick={() => handleViewFile(candidato.laudoMedicoUrl!)}
                  >
                    Visualizar
                  </button>
                  <button
                    className="btn btn-sm btn-secondary"
                    onClick={() => handleDownloadFile(candidato.laudoMedicoUrl!, 'laudo_medico.pdf')}
                  >
                    Baixar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* DOCUMENTOS OPCIONAIS */}
      {(candidato.documentosComplementaresUrl) && (
        <div className="card mb-4">
          <div className="card-header">
            <h5 className="mb-0">Documentos Opcionais</h5>
          </div>
          <div className="card-body">
            <div className="row">
              {candidato.documentosComplementaresUrl && (
                <div className="mb-3 col-md-12">
                  <label className="form-label fw-bold">Documentos Complementares Escolar:</label>
                  <div>
                    <button
                      className="btn btn-sm btn-primary me-2"
                      onClick={() => handleViewFile(candidato.documentosComplementaresUrl!)}
                    >
                      Visualizar
                    </button>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={() => handleDownloadFile(candidato.documentosComplementaresUrl!, 'documentos_complementares.pdf')}
                    >
                      Baixar
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* DECLARAÇÕES */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Declarações</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="mb-3 col-md-12">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={candidato.declaracaoVeracidade}
                  disabled
                />
                <label className="form-check-label">
                  Declaração de veracidade das informações prestadas
                </label>
              </div>
            </div>

            <div className="mb-3 col-md-12">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={candidato.confirmacaoEdital}
                  disabled
                />
                <label className="form-check-label">
                  Confirmação de ciência e concordância com o edital do processo seletivo
                </label>
              </div>
            </div>

            <div className="mb-3 col-md-12">
              <div className="form-check">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={candidato.consentimentoLgpd}
                  disabled
                />
                <label className="form-check-label">
                  Consentimento para tratamento de dados pessoais, nos termos da Lei Geral de Proteção de Dados (LGPD)
                </label>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* INFORMAÇÕES DO SISTEMA */}
      <div className="card mb-4">
        <div className="card-header">
          <h5 className="mb-0">Informações do Sistema</h5>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="mb-3 col-md-4">
              <label className="form-label fw-bold">Situação:</label>
              <p>{formatarSituacao(candidato.situacao)}</p>
            </div>

            <div className="mb-3 col-md-4">
              <label className="form-label fw-bold">Data de Inscrição:</label>
              <p>{formatarDataHora(candidato.dataInscricao)}</p>
            </div>

            <div className="mb-3 col-md-4">
              <label className="form-label fw-bold">Última Atualização:</label>
              <p>{formatarDataHora(candidato.editadoEm)}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

